import { ApiClient, ApiClientConfig } from 'Foundation/Api';
import { AuthenticationContextType } from 'Foundation/Authentication';
import { HttpResponseStatusCodes } from '@hobt/constants';
import { PricingScenarioData } from '../types';
import { config } from '../../../../../../../../config';
import {
  LockAndUnlockPayLoad,
  PricingScenarioPaylod
} from '../components/HbtMasterPoolAddModifyPricingScenariosFlyout/types';

export const HTTP_STATUS_CONFLICT = 409;

type RequestProps = {
  authenticationContext: AuthenticationContextType;
  data: { cmhcAccountNumber: number };
};

type addPricingScenarioProps = {
  authenticationContext: AuthenticationContextType;
  payload: PricingScenarioPaylod;
};
type patchOfferScenarioProps = {
  authenticationContext: AuthenticationContextType;
  masterPoolAccountNumber: number;
};

type modifyPricingScenarioProps = {
  authenticationContext: AuthenticationContextType;
  payload: PricingScenarioPaylod;
};
export type LockAndUnlockProps = {
  authenticationContext: AuthenticationContextType;
  payload: LockAndUnlockPayLoad;
};

const rethrowWorkflowError = (err: unknown, context: string): never => {
  if (err instanceof Error) {
    err.message = `${context}: ${err.message}`;
    throw err;
  }

  if (err && typeof err === 'object') {
    const wrappedError = new Error(context);
    (wrappedError as unknown as Record<string, unknown>).originalError = err;
    throw wrappedError;
  }

  throw new Error(`${context}: ${String(err)}`);
};

const apiClientConfig: ApiClientConfig = {
  timeout: config.validationMasterPoolAPi.requestTimeout
};

const individualLoanDetailsUrl: string =
  config.validationMasterPoolAPi.urls.masterPoolInventoryDetail;

const inventoryTimeout: number = config.validationReviewInventoryApi.requestTimeout;

export const getPricingScenario = async ({
  authenticationContext,
  data
}: RequestProps): Promise<PricingScenarioData | null> => {
  try {
    const requestUrl = `${individualLoanDetailsUrl}/${data.cmhcAccountNumber}/getPricingScenarios`;
    const response = await ApiClient(authenticationContext, apiClientConfig).getWithAuth(
      requestUrl
    );
    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data?.[0]) ||
      {}
    );
  } catch (err) {
    throw new Error('Error getting pricing scenarios');
    // eslint-disable-next-line no-unreachable
    return null;
  }
};

export const addPricingScenario = async ({
  authenticationContext,
  payload
}: addPricingScenarioProps) => {
  try {
    const requestUrl = `${individualLoanDetailsUrl}/${payload.masterPoolAccountNumber}/scenario`;
    const response = await ApiClient(authenticationContext, apiClientConfig).postWithAuth(
      requestUrl,
      payload,
      { timeout: inventoryTimeout }
    );
    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data?.[0]) ||
      {}
    );
  } catch (err) {
    throw new Error('Error while adding pricing scenarios');
  }
};

export const patchOfferScenario = async ({
  authenticationContext,
  masterPoolAccountNumber
}: patchOfferScenarioProps) => {
  try {
    const requestUrl = `${individualLoanDetailsUrl}/${masterPoolAccountNumber}/offer/scenario`;

    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      requestUrl,
      { masterPoolAccountNumber },
      { timeout: inventoryTimeout }
    );
    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data?.[0]) ||
      {}
    );
  } catch (err) {
    throw new Error('Error while patching pricing scenarios');
  }
};

export const modifyPricingScenario = async ({
  authenticationContext,
  payload
}: modifyPricingScenarioProps) => {
  try {
    const requestUrl = `${individualLoanDetailsUrl}/${payload.masterPoolAccountNumber}/scenario`;
    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      requestUrl,
      payload,
      { timeout: inventoryTimeout }
    );
    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data?.[0]) ||
      {}
    );
  } catch (err) {
    throw new Error('Error while modifying pricing scenarios');
  }
};

export const lockAndUnlockScenario = async ({
  authenticationContext,
  payload
}: LockAndUnlockProps) => {
  try {
    const { masterPoolAccountNumber, ...bodyPayload } = payload;

    const requestUrl = `${individualLoanDetailsUrl}/${payload.masterPoolAccountNumber}/lock/scenario`;
    const response = await ApiClient(authenticationContext, apiClientConfig).patchWithAuth(
      requestUrl,
      bodyPayload,
      { timeout: inventoryTimeout }
    );
    return (
      ((response?.data?.statusCode === HttpResponseStatusCodes.Created ||
        response?.data?.statusCode === HttpResponseStatusCodes.OK) &&
        response?.data?.data?.[0]) ||
      {}
    );
  } catch (err) {
    return rethrowWorkflowError(err, 'Error resetting workflow state');
  }
};
